import { Plus, Smartphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import type {
  BusinessDetailsView,
  LiveOwnerRow,
  OwnerAccessScope,
  OwnerCreateResult,
  OperationalBranch,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import {
  Badge,
  Button,
  ConfirmDialog,
  CopyButton,
  DescriptionList,
  EmptyState,
  FormField,
  Input,
  Modal,
  RadioGroup,
  Select,
  StatusBadge,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import {
  activateBusinessOwner,
  createBusinessOwnerAccount,
  deactivateBusinessOwner,
  resetBusinessOwnerPassword,
  updateBusinessOwnerAccess,
} from '@/data';
import { errorMessage } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/format';

function idemKey(): string {
  return crypto.randomUUID();
}

function accessLabel(scope: OwnerAccessScope, branchName?: string | null): string {
  if (scope === 'SPECIFIC_BRANCH') {
    return `Specific Branch — ${branchName?.trim() || 'Unknown branch'}`;
  }
  return 'All Branches';
}

function mappedCaBranches(branches: OperationalBranch[]): Array<{ caBranchId: string; name: string; productionBranchId: string }> {
  return branches
    .filter((b): b is OperationalBranch & { caBranchId: string; productionBranchId: string } =>
      Boolean(b.caBranchId && b.productionBranchId),
    )
    .map((b) => ({ caBranchId: b.caBranchId, name: b.name, productionBranchId: b.productionBranchId }));
}

function caBranchForProduction(branches: OperationalBranch[], productionBranchId: string | null): string {
  if (!productionBranchId) return '';
  return branches.find((b) => b.productionBranchId === productionBranchId)?.caBranchId ?? '';
}

type PanelMode = 'list' | 'create' | 'edit-access';
type SuccessKind = 'create' | 'reset' | null;

export function OwnerUsersManager({
  businessId,
  businessName,
  details,
  onRefresh,
}: {
  businessId: string;
  businessName: string;
  details: BusinessDetailsView;
  onRefresh: () => void | Promise<void>;
}) {
  const canManage = useCan()('mobile.manage');
  const productionAvailable = details.overview.productionAvailable === true;
  const ownersSection = details.owners;
  const accounts = ownersSection.accounts;
  const notLinked = ownersSection.availability === 'not_linked';
  const unavailable = ownersSection.availability === 'unavailable' || ownersSection.availability === 'forbidden';
  const mappedBranches = useMemo(
    () => mappedCaBranches(details.operationalBranches ?? []),
    [details.operationalBranches],
  );

  const [mode, setMode] = useState<PanelMode>('list');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [accessScope, setAccessScope] = useState<OwnerAccessScope>('ALL_BRANCHES');
  const [caBranchId, setCaBranchId] = useState('');
  const [editingOwner, setEditingOwner] = useState<LiveOwnerRow | null>(null);
  const [confirmAllBranches, setConfirmAllBranches] = useState(false);
  const [pendingAccess, setPendingAccess] = useState<{
    owner: LiveOwnerRow;
    accessScope: OwnerAccessScope;
    branchId: string;
  } | null>(null);

  const [successKind, setSuccessKind] = useState<SuccessKind>(null);
  const [successUsername, setSuccessUsername] = useState('');
  const [successPassword, setSuccessPassword] = useState<string | null>(null);
  const [successAccess, setSuccessAccess] = useState('');

  const openCreate = () => {
    setMode('create');
    setFormError(null);
    setFullName('');
    setUsername('');
    setAccessScope('ALL_BRANCHES');
    setCaBranchId(mappedBranches[0]?.caBranchId ?? '');
  };

  const openEditAccess = (owner: LiveOwnerRow) => {
    setEditingOwner(owner);
    setMode('edit-access');
    setFormError(null);
    setAccessScope(owner.accessScope ?? 'ALL_BRANCHES');
    setCaBranchId(caBranchForProduction(details.operationalBranches ?? [], owner.branchId) || mappedBranches[0]?.caBranchId || '');
  };

  const closeForms = () => {
    setMode('list');
    setEditingOwner(null);
    setFormError(null);
    setConfirmAllBranches(false);
    setPendingAccess(null);
  };

  const submitCreate = async () => {
    if (!fullName.trim() || !username.trim()) {
      setFormError('Enter a full name and username.');
      return;
    }
    if (accessScope === 'SPECIFIC_BRANCH' && !caBranchId) {
      setFormError('Select a Production-mapped branch.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const res: OwnerCreateResult = await createBusinessOwnerAccount(businessId, {
        username: username.trim(),
        fullName: fullName.trim(),
        accessScope,
        ...(accessScope === 'SPECIFIC_BRANCH' ? { branchId: caBranchId } : {}),
        idempotencyKey: idemKey(),
      });
      const branchName =
        accessScope === 'SPECIFIC_BRANCH'
          ? (res.branchName ?? mappedBranches.find((b) => b.caBranchId === caBranchId)?.name ?? null)
          : null;
      setSuccessKind('create');
      setSuccessUsername(res.username);
      setSuccessPassword(res.temporaryPassword ?? null);
      setSuccessAccess(accessLabel(res.accessScope ?? accessScope, branchName));
      closeForms();
      await onRefresh();
    } catch (error) {
      setFormError(errorMessage(error, 'Create Owner User failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const runAccessUpdate = async (owner: LiveOwnerRow, nextScope: OwnerAccessScope, nextCaBranchId: string) => {
    setSubmitting(true);
    setFormError(null);
    try {
      await updateBusinessOwnerAccess(businessId, owner.id, {
        accessScope: nextScope,
        ...(nextScope === 'SPECIFIC_BRANCH' ? { branchId: nextCaBranchId } : {}),
        idempotencyKey: idemKey(),
      });
      closeForms();
      await onRefresh();
    } catch (error) {
      setFormError(errorMessage(error, 'Update access failed'));
    } finally {
      setSubmitting(false);
      setConfirmAllBranches(false);
      setPendingAccess(null);
    }
  };

  const submitEditAccess = async () => {
    if (!editingOwner) return;
    if (accessScope === 'SPECIFIC_BRANCH' && !caBranchId) {
      setFormError('Select a Production-mapped branch.');
      return;
    }
    const increasingToAll =
      editingOwner.accessScope === 'SPECIFIC_BRANCH' && accessScope === 'ALL_BRANCHES';
    if (increasingToAll) {
      setPendingAccess({ owner: editingOwner, accessScope, branchId: caBranchId });
      setConfirmAllBranches(true);
      return;
    }
    await runAccessUpdate(editingOwner, accessScope, caBranchId);
  };

  const submitReset = async (owner: LiveOwnerRow) => {
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await resetBusinessOwnerPassword(businessId, owner.id, { idempotencyKey: idemKey() });
      setSuccessKind('reset');
      setSuccessUsername(res.username ?? owner.usernameMasked);
      setSuccessPassword(res.temporaryPassword ?? null);
      setSuccessAccess(accessLabel(owner.accessScope, owner.branchName));
      await onRefresh();
    } catch (error) {
      setFormError(errorMessage(error, 'Reset password failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitToggleActive = async (owner: LiveOwnerRow, activate: boolean) => {
    setSubmitting(true);
    setFormError(null);
    try {
      if (activate) {
        await activateBusinessOwner(businessId, owner.id, { idempotencyKey: idemKey() });
      } else {
        await deactivateBusinessOwner(businessId, owner.id, { idempotencyKey: idemKey() });
      }
      await onRefresh();
    } catch (error) {
      setFormError(errorMessage(error, activate ? 'Enable failed' : 'Disable failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (ownersSection.availability === 'forbidden') {
    return <EmptyState title="Not available for your role" description={ownersSection.message ?? undefined} />;
  }

  if (notLinked) {
    return (
      <EmptyState
        title="Cloud/Production setup required"
        description={
          ownersSection.message ??
          'Cloud/Production setup required before creating Owner User. Link a Production tenant to manage Owner Users for this business.'
        }
      />
    );
  }

  if (unavailable && accounts.length === 0) {
    return (
      <EmptyState
        title="Owner Users unavailable"
        description={ownersSection.message ?? 'Production data temporarily unavailable'}
      />
    );
  }

  return (
    <div className="space-y-4">
      {!productionAvailable && (
        <Badge tone="warning">Production temporarily unavailable — writes disabled</Badge>
      )}
      {formError && mode === 'list' && (
        <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>
      )}

      {accounts.length === 0 ? (
        <EmptyState
          title="No Owner Users"
          description="Create an Owner User to give the pharmacy owner access to the Owner App."
          action={
            canManage ? (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus size={14} aria-hidden="true" />}
                onClick={openCreate}
                disabled={!productionAvailable || notLinked || submitting}
              >
                Add Owner User
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-ink-secondary">
              {accounts.length} Owner User{accounts.length === 1 ? '' : 's'} for {businessName}
            </p>
            {canManage && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Plus size={14} aria-hidden="true" />}
                onClick={openCreate}
                disabled={!productionAvailable || submitting}
              >
                Add Owner User
              </Button>
            )}
          </div>
          <TableContainer label="Owner Users">
            <Table>
              <THead>
                <TR>
                  <TH>Full Name</TH>
                  <TH>Username</TH>
                  <TH>Access</TH>
                  <TH>Status</TH>
                  <TH>Last Login</TH>
                  {canManage && <TH align="right">Actions</TH>}
                </TR>
              </THead>
              <TBody>
                {accounts.map((owner) => (
                  <TR key={owner.id}>
                    <TD className="font-medium text-ink">{owner.fullName}</TD>
                    <TD className="font-mono text-xs text-ink-secondary">{owner.usernameMasked}</TD>
                    <TD className="text-ink-secondary">{accessLabel(owner.accessScope, owner.branchName)}</TD>
                    <TD>
                      <StatusBadge
                        tone={owner.isActive ? 'success' : 'neutral'}
                        label={owner.isActive ? 'Active' : 'Disabled'}
                        dot={false}
                      />
                    </TD>
                    <TD className="text-ink-secondary">
                      {owner.lastLoginAt ? (
                        <span title={formatDateTime(owner.lastLoginAt)}>{formatRelative(owner.lastLoginAt)}</span>
                      ) : (
                        '—'
                      )}
                    </TD>
                    {canManage && (
                      <TD align="right">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!productionAvailable || submitting}
                            onClick={() => void submitReset(owner)}
                          >
                            Reset Password
                          </Button>
                          {owner.isActive ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!productionAvailable || submitting}
                              onClick={() => void submitToggleActive(owner, false)}
                            >
                              Disable
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!productionAvailable || submitting}
                              onClick={() => void submitToggleActive(owner, true)}
                            >
                              Enable
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={!productionAvailable || submitting}
                            onClick={() => openEditAccess(owner)}
                          >
                            Edit Access
                          </Button>
                        </div>
                      </TD>
                    )}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!canManage && (
        <p className="text-[13px] text-ink-secondary">View only — your role cannot manage Owner Users (mobile.manage).</p>
      )}

      <Modal
        open={mode === 'create'}
        onClose={() => !submitting && closeForms()}
        title="Add Owner User"
        description={businessName}
        icon={<Smartphone size={18} aria-hidden="true" />}
        size="md"
        dismissible={!submitting}
        footer={
          <>
            <Button variant="secondary" onClick={closeForms} disabled={submitting} className="mr-auto">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submitCreate()}
              disabled={submitting || !fullName.trim() || !username.trim()}
              loading={submitting}
            >
              Create Owner User
            </Button>
          </>
        }
      >
        <OwnerAccessForm
          fullName={fullName}
          username={username}
          accessScope={accessScope}
          caBranchId={caBranchId}
          mappedBranches={mappedBranches}
          formError={formError}
          showIdentity
          onFullName={setFullName}
          onUsername={setUsername}
          onAccessScope={setAccessScope}
          onCaBranchId={setCaBranchId}
        />
      </Modal>

      <Modal
        open={mode === 'edit-access' && Boolean(editingOwner)}
        onClose={() => !submitting && closeForms()}
        title="Edit Access"
        description={editingOwner ? `${editingOwner.fullName} (${editingOwner.usernameMasked})` : businessName}
        icon={<Smartphone size={18} aria-hidden="true" />}
        size="md"
        dismissible={!submitting}
        footer={
          <>
            <Button variant="secondary" onClick={closeForms} disabled={submitting} className="mr-auto">
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void submitEditAccess()} disabled={submitting} loading={submitting}>
              Save Access
            </Button>
          </>
        }
      >
        <OwnerAccessForm
          fullName=""
          username=""
          accessScope={accessScope}
          caBranchId={caBranchId}
          mappedBranches={mappedBranches}
          formError={formError}
          showIdentity={false}
          onFullName={() => undefined}
          onUsername={() => undefined}
          onAccessScope={setAccessScope}
          onCaBranchId={setCaBranchId}
        />
      </Modal>

      <ConfirmDialog
        open={confirmAllBranches && Boolean(pendingAccess)}
        title="Grant All Branches access?"
        message="This Owner User will be able to see every branch under this Cloud POS account, including branches added later. Continue?"
        confirmLabel="Grant All Branches"
        tone="primary"
        loading={submitting}
        onCancel={() => {
          if (!submitting) {
            setConfirmAllBranches(false);
            setPendingAccess(null);
          }
        }}
        onConfirm={() => {
          if (pendingAccess) {
            void runAccessUpdate(pendingAccess.owner, pendingAccess.accessScope, pendingAccess.branchId);
          }
        }}
      />

      <Modal
        open={successKind !== null}
        onClose={() => {
          setSuccessKind(null);
          setSuccessPassword(null);
        }}
        title={successKind === 'reset' ? 'Password reset' : 'Owner User created'}
        description={businessName}
        icon={<Smartphone size={18} aria-hidden="true" />}
        size="md"
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setSuccessKind(null);
              setSuccessPassword(null);
            }}
          >
            Done
          </Button>
        }
      >
        <div className="space-y-4">
          <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-[13px] text-warning">
            Temporary password is shown once and is not stored in Central Admin. Copy it before closing.
          </p>
          <DescriptionList
            items={[
              { label: 'Username', value: <span className="font-mono text-xs">{successUsername}</span> },
              { label: 'Access', value: successAccess },
              {
                label: 'Temporary password',
                value: successPassword ? (
                  <span className="font-mono text-xs">{successPassword}</span>
                ) : (
                  'Not returned by Production'
                ),
              },
            ]}
          />
          <div className="flex flex-wrap gap-2">
            <CopyButton value={successUsername} label="Copy Username" />
            <CopyButton value={successPassword ?? ''} label="Copy Password" disabled={!successPassword} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function OwnerAccessForm({
  fullName,
  username,
  accessScope,
  caBranchId,
  mappedBranches,
  formError,
  showIdentity,
  onFullName,
  onUsername,
  onAccessScope,
  onCaBranchId,
}: {
  fullName: string;
  username: string;
  accessScope: OwnerAccessScope;
  caBranchId: string;
  mappedBranches: Array<{ caBranchId: string; name: string }>;
  formError: string | null;
  showIdentity: boolean;
  onFullName: (v: string) => void;
  onUsername: (v: string) => void;
  onAccessScope: (v: OwnerAccessScope) => void;
  onCaBranchId: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      {formError && (
        <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">{formError}</p>
      )}
      {showIdentity && (
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Full Name" htmlFor="owner-user-fullname">
            <Input
              id="owner-user-fullname"
              value={fullName}
              onChange={(e) => onFullName(e.target.value)}
              autoComplete="off"
            />
          </FormField>
          <FormField label="Username" htmlFor="owner-user-username">
            <Input
              id="owner-user-username"
              value={username}
              onChange={(e) => onUsername(e.target.value)}
              autoComplete="off"
            />
          </FormField>
        </div>
      )}
      <RadioGroup
        name="owner-access-scope"
        legend="Access"
        value={accessScope}
        onChange={onAccessScope}
        columns={1}
        options={[
          {
            value: 'ALL_BRANCHES',
            label: 'All Branches',
            description: 'Owner can access every branch under this Cloud POS account (including future branches).',
          },
          {
            value: 'SPECIFIC_BRANCH',
            label: 'Specific Branch',
            description: 'Owner is limited to one Production-mapped branch.',
            disabled: mappedBranches.length === 0,
          },
        ]}
      />
      {accessScope === 'SPECIFIC_BRANCH' && (
        <FormField label="Branch" htmlFor="owner-user-branch">
          {mappedBranches.length === 0 ? (
            <p className="text-sm text-ink-secondary">
              No Production-mapped branches are available. Connect a branch to Cloud POS first.
            </p>
          ) : (
            <Select
              id="owner-user-branch"
              value={caBranchId}
              onChange={(e) => onCaBranchId(e.target.value)}
              options={mappedBranches.map((b) => ({ value: b.caBranchId, label: b.name }))}
            />
          )}
        </FormField>
      )}
      {accessScope === 'ALL_BRANCHES' && (
        <p className="text-[13px] text-ink-secondary">Default access is All Branches.</p>
      )}
    </div>
  );
}
